import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, BadgeCheck, Play, ChevronLeft, ChevronRight } from "lucide-react";

const REVIEWS_WITH_TEXT = [
  {
    id: "r1",
    username: "aash",
    rating: 5,
    text: "+rep, lwk got an office laptop, tweaked it and now getting 120+ fps, before I was getting like 40-50 with a lot of fps drops. like you must buy ts gngo, genuinely. edit: NO FPS DROPSSS!! IN BR",
  },
  {
    id: "r2",
    username: "faded874",
    rating: 5,
    text: "went from 400 fps to 650, processes went from 180 to 135, rlly good tweaker give him a chance",
  },
  {
    id: "r3",
    username: "Flex",
    rating: 5,
    text: "V good tweaks capped at 1250 fps love Flow and very cheap",
  },
  {
    id: "r4",
    username: "copper",
    rating: 5,
    text: "1. tweaked me up 2. my bios was autistic 3. but it fine 4. BCUZ FLOW IS THE GOAT 5. GOAT 6. GOAT FLOW 7. GOAT 8. FLOW",
  },
];

const REVIEWS_NAME_ONLY = [
  { id: "n1", username: "civko" },
  { id: "n2", username: "bunki" },
  { id: "n3", username: "lyric" },
  { id: "n4", username: "maps" },
  { id: "n5", username: "ryze" },
  { id: "n6", username: "pluto" },
  { id: "n7", username: "astro" },
];

const VIDEO_REVIEWS = [
  // preload="none" on the <video> keeps ~9MB off first load; the poster is what fills the card
  // until someone actually presses play.
  { id: "v1", username: "civko", src: "/video1.mp4", poster: "/poster-video1.jpg" },
  { id: "v2", username: "lyric", src: "/video2.mp4", poster: "/poster-video2.jpg" },
  { id: "v3", username: "astro", src: "/video3.mp4", poster: "/poster-video3.jpg" },
  { id: "v4", username: "hiro", src: "/video4.mp4", poster: "/poster-video4.jpg" },
];

const Stars = ({ n }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < n ? "fill-orange-500 text-orange-500" : "text-zinc-700"}`}
      />
    ))}
  </div>
);

// Index-driven, not scroll-driven — the whole slide advances on click/tick, nothing depends on
// native scroll position, scroll-snap, or browser hover-state edge cases.
const TestimonialSlider = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const count = REVIEWS_WITH_TEXT.length;

  // A self-resetting timeout keyed on `index`, not a free-running interval — every manual
  // navigation changes `index`, which cancels whatever auto-advance was pending and schedules a
  // fresh one 5s out. A plain setInterval kept ticking in the background independent of manual
  // clicks, so a click and a background tick could land close together and skip a slide.
  useEffect(() => {
    if (paused) return undefined;
    const id = setTimeout(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearTimeout(id);
  }, [index, paused, count]);

  const go = (dir) => {
    setDirection(dir);
    setIndex((i) => (i + dir + count) % count);
  };

  const jumpTo = (i) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };

  const r = REVIEWS_WITH_TEXT[index];

  return (
    <div
      className="relative mx-auto max-w-3xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      data-testid="testimonial-slider"
    >
      <div className="glass glass-sheen !rounded-3xl relative flex min-h-[280px] flex-col items-center overflow-hidden p-8 text-center sm:min-h-[260px] sm:p-12">
        <Quote className="mb-4 h-8 w-8 shrink-0 text-orange-500/40" />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={r.id}
            initial={{ opacity: 0, x: direction * 32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -32 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="flex flex-col items-center"
            data-testid={`testimonial-slide-${r.id}`}
          >
            <Stars n={r.rating} />
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-200 sm:text-lg">
              "{r.text}"
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="glass !rounded-lg flex h-10 w-10 shrink-0 items-center justify-center text-sm font-black text-orange-500">
                {r.username.slice(0, 2).toUpperCase()}
              </div>
              <div className="text-left">
                <div className="text-sm font-bold tracking-wide text-white">@{r.username}</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-6 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous testimonial"
          className="glass glass-hover flex h-10 w-10 shrink-0 items-center justify-center !rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2">
          {REVIEWS_WITH_TEXT.map((tr, i) => (
            <button
              key={tr.id}
              type="button"
              onClick={() => jumpTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-6 bg-orange-500" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next testimonial"
          className="glass glass-hover flex h-10 w-10 shrink-0 items-center justify-center !rounded-full"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const VideoCard = ({ r }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handleClick = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
  };

  return (
    <div
      className={`glass glass-hover !rounded-2xl relative overflow-hidden group ${playing ? "!border-orange-500/70" : ""}`}
      data-testid={`review-video-${r.id}`}
    >
      <div className="relative aspect-[9/16] cursor-pointer" onClick={handleClick}>
        <video
          ref={videoRef}
          src={r.src}
          poster={r.poster}
          preload="none"
          className="h-full w-full object-cover"
          onEnded={handleEnded}
          playsInline
          aria-label={`Video review from @${r.username}`}
        />
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 shadow-lg shadow-orange-500/30 transition-colors hover:bg-orange-400">
              <Play className="ml-1 h-6 w-6 fill-black text-black" />
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 border-t border-white/5 p-4">
        <div className="glass !rounded-lg flex h-8 w-8 shrink-0 items-center justify-center text-xs font-black text-orange-500">
          {r.username.slice(0, 2).toUpperCase()}
        </div>
        <div>
          <div className="text-sm font-bold tracking-wide text-white">@{r.username}</div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500">
            <BadgeCheck className="h-3 w-3" /> Verified
          </span>
        </div>
      </div>
    </div>
  );
};

const NamePill = ({ r }) => (
  <div
    className="glass glass-hover !rounded-full flex items-center gap-2.5 py-2 pl-2.5 pr-4"
    data-testid={`review-name-${r.id}`}
  >
    <div className="glass !rounded-full flex h-7 w-7 shrink-0 items-center justify-center text-[10px] font-black text-orange-500">
      {r.username.slice(0, 2).toUpperCase()}
    </div>
    <span className="text-xs font-bold tracking-wide text-white">@{r.username}</span>
    <Stars n={5} />
  </div>
);

export const Reviews = () => {
  return (
    <section id="reviews" className="relative py-24 sm:py-32 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4 slash">
            From the Community
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Real players. <br />
            <span className="text-orange-500">Real frames.</span>
          </h2>
          <p className="mt-5 text-zinc-500 text-sm uppercase tracking-[0.25em] font-bold">
            27+ Verified Positive Reviews
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-16">
        <TestimonialSlider />

        <div>
          <p className="mb-6 text-center text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">
            — Click to Play —
          </p>
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
            {VIDEO_REVIEWS.map((r) => (
              <VideoCard key={r.id} r={r} />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          {REVIEWS_NAME_ONLY.map((r) => (
            <NamePill key={r.id} r={r} />
          ))}
        </div>
      </div>
    </section>
  );
};
